// OutboundCreateRuleModal.jsx

import React from 'react';

// Utilities
import { cleanString } from '../../../apiUtils';
import { normalizeRuleEvent } from '../../utils/integrationUtils';

// Constants
import { RULE_EVENT_OPTIONS } from '../../constants/integrationOptions';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import ModalActions from './ModalActions';

/**
 * OutboundCreateRuleModal
 *
 * Renders the modal used to create a new outbound integration rule.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Handler to close the modal.
 * @param {boolean} canManageCredentials - Whether the user can manage outbound rules.
 * @param {string} busyActionKey - Current busy action key used to disable actions.
 * @param {string} draftName - Draft rule name.
 * @param {string} draftEvent - Draft trigger event.
 * @param {string} draftTypeId - Draft type id.
 * @param {string} draftDestinationUrl - Draft destination url.
 * @param {string} draftDestinationName - Draft destination name.
 * @param {array} typeRows - Available type options.
 * @param {function} onDraftNameChange - Handler to update draft name.
 * @param {function} onDraftEventChange - Handler to update draft event.
 * @param {function} onDraftTypeIdChange - Handler to update draft type id.
 * @param {function} onDraftDestinationUrlChange - Handler to update draft destination url.
 * @param {function} onDraftDestinationNameChange - Handler to update draft destination name.
 * @param {function} onCreateRule - Handler to create rule.
 *
 * @returns {ReactNode} The rendered create outbound rule modal.
 */
const OutboundCreateRuleModal = ({
    theme,
    isOpen,
    onClose,
    canManageCredentials,
    busyActionKey,
    draftName,
    draftEvent,
    draftTypeId,
    draftDestinationUrl,
    draftDestinationName,
    typeRows,
    onDraftNameChange,
    onDraftEventChange,
    onDraftTypeIdChange,
    onDraftDestinationUrlChange,
    onDraftDestinationNameChange,
    onCreateRule,
}) => {

    // ----- RENDER ----- //

    return (
        <Modal
            title="Add Outbound Rule"
            isOpen={isOpen}
            onClose={onClose}
            width="620px"
            maxHeight="85vh"
        >
            <div className="api-integrations-modal-body">
                {/* INTRO */}
                <div
                    className="api-integrations-subtitle"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Configure when Orbital should call an external webhook destination.
                </div>

                {/* FORM */}
                <div className="api-integrations-form-grid">
                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Rule name
                    </div>
                    <TextInput
                        value={draftName}
                        onChange={(event) => onDraftNameChange(event.target.value)}
                        placeholder="Example: Tasks -> Zapier Sheet"
                        disabled={!canManageCredentials}
                    />

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Trigger event
                    </div>
                    <SelectInput
                        value={draftEvent}
                        onChange={(event) => onDraftEventChange(normalizeRuleEvent(event.target.value))}
                        disabled={!canManageCredentials}
                    >
                        {RULE_EVENT_OPTIONS.map((row) => (
                            <option key={row.value} value={row.value}>{row.label}</option>
                        ))}
                    </SelectInput>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Trigger type
                    </div>
                    <SelectInput
                        value={draftTypeId}
                        onChange={(event) => onDraftTypeIdChange(cleanString(event.target.value))}
                        disabled={!canManageCredentials || typeRows.length === 0}
                    >
                        {typeRows.map((row) => (
                            <option key={row.typeId} value={row.typeId}>{row.label}</option>
                        ))}
                    </SelectInput>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Destination URL
                    </div>
                    <TextInput
                        value={draftDestinationUrl}
                        onChange={(event) => onDraftDestinationUrlChange(event.target.value)}
                        placeholder="https://hooks.zapier.com/hooks/catch/..."
                        disabled={!canManageCredentials}
                    />

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Destination name (optional)
                    </div>
                    <TextInput
                        value={draftDestinationName}
                        onChange={(event) => onDraftDestinationNameChange(event.target.value)}
                        placeholder="Zapier Catch Hook"
                        disabled={!canManageCredentials}
                    />
                </div>

                {/* ACTIONS */}
                <ModalActions>
                    <Button
                        label="Cancel"
                        onClick={onClose}
                        enabled={busyActionKey !== 'create-rule'}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Create Rule"
                        onClick={onCreateRule}
                        enabled={canManageCredentials && busyActionKey !== 'create-rule'}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                </ModalActions>
            </div>
        </Modal>
    );
};

// Dev Tools
OutboundCreateRuleModal.displayName = 'OutboundCreateRuleModal';

export default OutboundCreateRuleModal;
